import { useState, useEffect } from 'react'
import { Volume2, X, Smartphone } from 'lucide-react'

interface MobileAudioGuidanceProps {
  isVisible: boolean
  onDismiss: () => void
  callType: 'voice' | 'video' | 'screen'
}

/**
 * Mobile Audio Guidance Component
 * Shows instructions to users on mobile devices when audio might not be playing
 */
export function MobileAudioGuidance({
  isVisible,
  onDismiss,
  callType
}: MobileAudioGuidanceProps) {
  const [isShowing, setIsShowing] = useState(false)

  useEffect(() => {
    if (isVisible) {
      setIsShowing(true)
    }
  }, [isVisible])

  const handleDismiss = () => {
    setIsShowing(false)
    setTimeout(onDismiss, 300) // Allow fade animation to complete
  }

  if (!isShowing) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full mx-4 transform transition-all duration-300 ease-out">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Volume2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Audio Setup</h3>
                <p className="text-sm text-gray-600">Enable {callType} call audio</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Smartphone className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 mb-2">
                Can't hear the other person?
              </h4>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  Mobile browsers require a tap to enable audio playback.
                </p>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="font-medium text-blue-900 mb-1">Quick fix:</p>
                  <p className="text-blue-800">
                    Tap anywhere on the screen to unlock audio
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  This is a browser security feature to prevent unwanted audio playback.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0">
          <button
            onClick={handleDismiss}
            className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}